import React, { useCallback, useMemo } from "react";

import { Activity } from "@mapmycustomers/shared/types/entity";
import { RawFile } from "@mapmycustomers/shared/types/File";

import FileUploader from "@app/component/FileUploader";
import { removeActivityFile } from "@app/scene/activity/store/actions";
import FileListItem from "@app/types/FileListItem";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { activityLayoutModel } from "@app/util/layout/impl";
import useEntityLayoutSchema from "@app/util/useEntityLayoutSchema";

interface Props {
  activity: Activity;
  associatedFiles?: RawFile[];
  filePreview?: Blob;
  filePreviewId?: RawFile["id"];
  filePreviewLoading?: boolean;
  onFetchFilePreview?: (payload: { activityId: Activity["id"]; file: RawFile["id"] }) => void;
  onFileUpload: (payload: {
    activityId: Activity["id"];
    callback: (filesList: FileListItem[]) => void;
    files: (Blob | File)[];
  }) => void;
  onRemoveFile: typeof removeActivityFile.request;
  updatedActivity: (activity: Activity) => void;
}

const ActivityFileUploader: React.FC<Props> = ({
  activity,
  associatedFiles,
  filePreview,
  filePreviewId,
  filePreviewLoading,
  onFetchFilePreview,
  onFileUpload,
  onRemoveFile,
  updatedActivity,
}) => {
  const layout = useMemo(() => activityLayoutModel.getLayoutFor(activity), [activity]);
  const [schema] = useEntityLayoutSchema({ entity: activity, layout });
  const filesSchema = useMemo(
    () => schema.find((schemaField) => schemaField.field === ActivityFieldName.FILES),
    [schema]
  );

  const handleRemove = useCallback(
    (file: RawFile) =>
      onRemoveFile?.({
        activity: activity,
        file: file,
        onSuccess: (activity: Activity) => {
          updatedActivity(activity);
        },
      }),
    [activity, onRemoveFile, updatedActivity]
  );

  const handleUpload = useCallback(
    (payload: { callback: (filesList: FileListItem[]) => void; files: (Blob | File)[] }) => {
      onFileUpload({
        activityId: activity.id,
        callback: payload.callback,
        files: payload.files,
      });
    },
    [activity.id, onFileUpload]
  );

  const handleFilePreview = useCallback(
    (fileId: RawFile["id"]) => {
      onFetchFilePreview?.({ activityId: activity.id, file: fileId });
    },
    [activity, onFetchFilePreview]
  );

  return (
    <FileUploader
      associatedFiles={associatedFiles}
      filePreview={filePreview}
      filePreviewId={filePreviewId}
      filePreviewLoading={filePreviewLoading}
      onFetchFilePreview={handleFilePreview}
      onFileUpload={handleUpload}
      onRemoveFile={handleRemove}
      required={filesSchema?.required}
      supportsTags
      validateLastRequiredFileRemove
    />
  );
};

export default ActivityFileUploader;
